import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  setGraphStatus,
  setDtInfoMenuOpen,
  setDtInfoMode
} from '../../../slice/authSlice';
import { Icon } from '../../icons';
import AssessmentIcon from '@mui/icons-material/Assessment';

const SidebarItem = ({ onClick, active, disabled, children, label, isBlinking, isDanger }) => {
  return (
    <div className="relative group flex flex-col items-center justify-center cursor-pointer transition-colors duration-200 hover:bg-slate-800/50">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`p-2 rounded-xl transition-all duration-200 flex flex-col items-center gap-1 ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
          } ${active ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-400 hover:text-indigo-300'}`}
      >
        <div className={`${active ? (isDanger ? 'text-red-500' : 'text-indigo-400') : ''}`}>
          {children}
        </div>
        {label && <span className="text-[11px] font-bold tracking-tight mt-1">{label}</span>}
      </button>
    </div>
  );
};

export default function SPCSidebar({ handleLogout, onDtInfoModeChange, handleFireAlarm, onOpenCertificate }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    graphStatus,
    disasterStatus,
    dtInfoMenuOpen,
    dtInfoMode,
  } = useSelector((state) => state.auth);

  // Home Navigation
  const handleHome = () => {
    navigate('/projects');
  };

  // Feature Toggles
  const toggleGraph = () => {
    if (disasterStatus.isActive) return;
    dispatch(setGraphStatus(!graphStatus));
  };

  // Info Mode Navigation
  const openInfoMenu = () => {
    dispatch(setDtInfoMenuOpen(true));
    dispatch(setDtInfoMode(0));
    onDtInfoModeChange(0);
  };

  const handleModeSelect = (mode) => {
    dispatch(setDtInfoMode(mode));
    onDtInfoModeChange(mode);
  };

  const handleCertificate = () => {
    onOpenCertificate();
  };

  const closeInfoMenu = () => {
    dispatch(setDtInfoMenuOpen(false));
  };

  return (
    <div className="w-[80px] h-full bg-slate-900 flex flex-col border-r border-slate-800 z-50 shadow-2xl">
      {/* Logo Area */}
      <div className="h-20 flex items-center justify-center border-b border-slate-800/50">
        <img src="/images/ese_logo.png" alt="Logo" className="w-10 opacity-90" />
      </div>

      {/* Main Menu Area */}
      {/* Scrollbar styling: thin, dark thumb, transparent track */}
      <div className="flex-1 overflow-y-auto space-y-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">

        {/* Navigation Group */}
        <div className="mb-6">
          <div className="text-[11px] text-slate-500 font-bold uppercase text-center mb-2 tracking-wider">이동</div>
          <SidebarItem onClick={handleHome} label={t("sidebars.dt.home")}>
            <Icon name="homeIcon" style={{ width: 24, height: 24 }} />
          </SidebarItem>
        </div>

        {/* DT Info Group */}
        {!dtInfoMenuOpen ? (
          <div className="mb-6">
            <div className="text-[11px] text-slate-500 font-bold uppercase text-center mb-2 tracking-wider">DT 메뉴</div>

            <SidebarItem onClick={toggleGraph} active={graphStatus} disabled={disasterStatus.isActive} label="대시보드">
              <AssessmentIcon style={{ width: 24, height: 24 }} />
            </SidebarItem>

            {/* Fire Alarm (Placeholder for logic) */}
            <SidebarItem onClick={handleFireAlarm} active={disasterStatus.isActive} isBlinking={disasterStatus.isActive} label="화재경보" isDanger>
              <Icon name="fireIcon" style={{ width: 24, height: 24 }} />
            </SidebarItem>

            {/* Info Mode Toggle */}
            <SidebarItem onClick={openInfoMenu} label="말풍선 모드">
              <Icon name="messageIcon" style={{ width: 24, height: 24 }} />
            </SidebarItem>

            {/* Certificate */}
            <SidebarItem onClick={handleCertificate} label="자율안전인증">
              <Icon name="certificateIcon" style={{ width: 24, height: 24 }} />
            </SidebarItem>
          </div>
        ) : (
          <div className="mb-6 animate-fade-in-right">
            <div className="text-[11px] text-slate-500 font-bold uppercase text-center mb-2 tracking-wider">
              말풍선 모드
            </div>

            {[0, 1, 2, 3, 4, 5].map((mode) => (
              <SidebarItem
                key={mode}
                onClick={() => handleModeSelect(mode)}
                active={dtInfoMode === mode}
                label={mode === 0 ? "Default" : `Mode ${mode}`}
              >
                <Icon name="messageIcon" style={{ width: 20, height: 20 }} />
              </SidebarItem>
            ))}

            <div className="pt-2 border-t border-slate-800 mt-2">
              <SidebarItem onClick={closeInfoMenu} label="뒤로가기">
                <Icon name="arrowLeftIcon" style={{ width: 20, height: 20 }} />
              </SidebarItem>
            </div>
          </div>
        )}

      </div>

      {/* Bottom Area */}
      <div className="py-4 border-t border-slate-800 bg-slate-900/50">
        <div className="flex justify-center">
          <button
            onClick={handleLogout}
            className="flex flex-col items-center text-slate-500 hover:text-red-400 transition-colors group"
            title="Logout"
          >
            <Icon
              name="logoutIcon"
              style={{ width: "24px", height: "24px" }}
            />
            <span className="text-[11px] font-bold mt-1">로그아웃</span>
          </button>
        </div>
      </div>
    </div>
  );
}
